import axios from 'axios'
import { platformEnv } from '@/platform/config/env'
import { attachAuthInterceptors } from '@/platform/http/authBridge'
import { createHttpClient } from '@/platform/http/client'
import { nextAssetVersionUrl } from '@/platform/media/assetVersion'
import type { Subject, SubjectDraft, SubjectStatus } from '../model/subject'

const client = createHttpClient(platformEnv.courseApiUrl)
const mediaClient = axios.create({ baseURL: platformEnv.gatewayApiUrl, timeout: 60_000 })
attachAuthInterceptors(mediaClient)

export type SubjectImageUploadStage = 'converting' | 'presigning' | 'uploading'

async function convertImageToAvif(file: Blob): Promise<Blob> {
  if (file.type === 'image/avif') return file
  const data = new FormData()
  data.append('file', file, 'subject-image')
  data.append('format', 'avif')
  const response = await mediaClient.post<Blob>('/v1/admin/media/image/convert', data, { responseType: 'blob' })
  return new Blob([response.data], { type: 'image/avif' })
}

export const subjectsApi = {
  async list(): Promise<Subject[]> {
    return (await client.request<Subject[]>({ url: '/v1/admin/subjects', method: 'GET' })).data
  },
  async create(draft: SubjectDraft): Promise<Subject> {
    return (await client.request<Subject>({ url: '/v1/admin/subjects', method: 'POST', data: draft })).data
  },
  async update(id: number, draft: Partial<SubjectDraft>): Promise<Subject> {
    return (await client.request<Subject>({ url: `/v1/admin/subjects/${id}`, method: 'PATCH', data: draft })).data
  },
  async setStatus(id: number, status: SubjectStatus): Promise<Subject> {
    return (await client.request<Subject>({ url: `/v1/admin/subjects/${id}`, method: 'PATCH', data: { status } })).data
  },
  async remove(id: number): Promise<void> {
    await client.request<void>({ url: `/v1/admin/subjects/${id}`, method: 'DELETE' })
  },
  async uploadImage(id: number, file: Blob, onStage?: (stage: SubjectImageUploadStage) => void, currentUrl?: string | null): Promise<Subject> {
    onStage?.('converting')
    const image = await convertImageToAvif(file)
    onStage?.('presigning')
    const target = (await client.request<{ upload_url: string; url: string; content_type: string }>({
      url: '/v1/admin/storage/presign',
      method: 'POST',
      data: { content_type: 'image/avif', folder: 'courses/subject', file_id: `${id}/image`, extension: 'avif' },
    })).data
    onStage?.('uploading')
    let response: Response
    try {
      response = await fetch(target.upload_url, { method: 'PUT', headers: { 'Content-Type': target.content_type }, body: image })
    } catch {
      throw new Error('Không thể kết nối kho ảnh. Hãy kiểm tra mạng rồi thử lại.')
    }
    if (!response.ok) throw new Error(`Tải ảnh lên R2 thất bại (${response.status}).`)
    return this.update(id, { icon: nextAssetVersionUrl(target.url, currentUrl) })
  },
  async removeImage(subject: Subject): Promise<Subject> {
    if (subject.icon) await client.request<void>({ url: '/v1/admin/storage/delete', method: 'POST', data: { url: subject.icon } })
    return this.update(subject.id, { icon: '' })
  },
}
